(() => {
  const ROOT_SELECTOR = '[data-product-variant-image-picker]';
  const DATA_SELECTOR = '[data-product-variant-image-picker-data]';
  const GENERATED_SELECTOR = '[data-generated-variant-image-picker]';
  const GENERATED_CONSENT_SELECTOR = '[data-generated-digital-consent]';
  const SELECT_SELECTOR = '[data-product-variant-select]';
  const SUBMIT_SELECTOR = '[data-product-submit]';
  const SELECT_HANDLER_KEY = '__qVariantImagePickerChangeHandler';
  const CONSENT_CLEANUP_KEY = '__qDigitalConsentCleanup';
  const MODULE_KEY = '__qVariantImagePickerModule';
  const DIGITAL_TYPE = Symbol('digital');
  const CONSENT_PENDING_CLASS = 'q-product-form--digital-consent-pending';

  const isNonEmptyString = (value) => typeof value === 'string' && value.trim().length > 0;
  const isValidPublicPropertyName = (value) => (
    isNonEmptyString(value)
    && !value.trim().startsWith('_')
    && !/[\[\]]/.test(value.trim())
  );
  const isValidVariantId = (value) => (
    (typeof value === 'number' && Number.isFinite(value) && Number.isInteger(value) && value > 0)
    || (typeof value === 'string' && /^[0-9]+$/.test(value))
  );
  const isValidRawVariant = (variant) => (
    Boolean(variant)
    && typeof variant === 'object'
    && !Array.isArray(variant)
    && isValidVariantId(variant.id)
    && typeof variant.available === 'boolean'
    && Array.isArray(variant.options)
    && variant.options.length >= 1 && variant.options.length <= 3
    && variant.options.every(isNonEmptyString)
    && (variant.imageUrl == null || typeof variant.imageUrl === 'string')
  );
  const hasValidDigitalConsentConfig = (digitalConsent) => (
    Boolean(digitalConsent)
    && typeof digitalConsent === 'object'
    && !Array.isArray(digitalConsent)
    && typeof digitalConsent.enabled === 'boolean'
    && (
      !digitalConsent.enabled
      || (
        isNonEmptyString(digitalConsent.text)
        && isValidPublicPropertyName(digitalConsent.propertyName)
        && isNonEmptyString(digitalConsent.propertyValue)
      )
    )
  );
  const hasValidRawConfig = (config) => (
    Array.isArray(config.optionNames)
    && config.optionNames.every(isNonEmptyString)
    && Array.isArray(config.physicalTypes)
    && config.physicalTypes.every(isNonEmptyString)
    && Array.isArray(config.colorValues)
    && config.colorValues.every(isNonEmptyString)
    && [
      config.typeOptionName,
      config.colorOptionName,
      config.digitalValue,
      config.digitalLabel,
    ].every(isNonEmptyString)
    && Array.isArray(config.variants)
    && hasValidDigitalConsentConfig(config.digitalConsent)
  );
  const trimmedList = (values) => values.map((value) => value.trim());

  const firstAvailable = (variants) => variants.find((variant) => variant.available) || null;

  const hasExactVariantStructure = ({
    rawOptionNames,
    optionNames,
    rawPhysicalTypes,
    physicalTypes,
    rawColorValues,
    colorValues,
    digitalValue,
    variants,
    typeIndex,
    colorIndex,
    customColorValue = '',
  }) => {
    if (optionNames.length === 3) {
      return new Set(optionNames).size === 3
        && typeIndex >= 0 && colorIndex >= 0 && typeIndex !== colorIndex
        && physicalTypes.length > 0 && new Set(physicalTypes).size === physicalTypes.length
        && colorValues.length > 0 && new Set(colorValues).size === colorValues.length
        && !colorValues.includes(digitalValue)
        && variants.length > 0
        && new Set(variants.map(variant => variant.id)).size === variants.length
        && new Set(variants.map(variant => JSON.stringify(variant.options))).size === variants.length
        && variants.every(variant => variant.options.length === 3
          && physicalTypes.includes(variant.options[typeIndex])
          && [...colorValues, digitalValue, customColorValue].filter(Boolean).includes(variant.options[colorIndex]));
    }
    if (optionNames.length === 1) {
      const allowedValues = new Set([...colorValues, digitalValue]);
      const values = variants.map((variant) => variant.options[0]);
      return rawOptionNames.length === 1
        && colorIndex === 0
        && typeIndex === -1
        && rawPhysicalTypes.length === 0
        && colorValues.length > 0
        && new Set(colorValues).size === colorValues.length
        && !colorValues.includes(digitalValue)
        && variants.length > 0
        && new Set(variants.map((variant) => variant.id)).size === variants.length
        && new Set(values).size === values.length
        && variants.every((variant) => variant.options.length === 1 && allowedValues.has(variant.options[0]));
    }

    if (
      rawOptionNames.length !== 2
      || optionNames.length !== 2
      || typeIndex < 0
      || colorIndex < 0
      || typeIndex === colorIndex
      || rawPhysicalTypes.length < 1
      || physicalTypes.length !== rawPhysicalTypes.length
      || new Set(physicalTypes).size !== physicalTypes.length
      || rawColorValues.length !== 3
      || colorValues.length !== 3
      || new Set(colorValues).size !== 3
      || !digitalValue
      || colorValues.includes(digitalValue)
      || variants.length === 0
      || new Set(variants.map((variant) => variant.id)).size !== variants.length
    ) return false;

    const expectedPhysicalKeys = new Set();
    physicalTypes.forEach((type) => {
      colorValues.forEach((color) => expectedPhysicalKeys.add(JSON.stringify([type, color])));
    });

    const seenPhysicalKeys = new Set();
    const seenDigitalTypes = new Set();
    const seenCustomTypes = new Set();

    for (const variant of variants) {
      if (variant.options.length !== 2) return false;

      const type = variant.options[typeIndex];
      const color = variant.options[colorIndex];
      if (!physicalTypes.includes(type)) return false;

      if (color === digitalValue) {
        if (seenDigitalTypes.has(type)) return false;
        seenDigitalTypes.add(type);
        continue;
      }

      if (customColorValue && color === customColorValue) {
        if (seenCustomTypes.has(type)) return false;
        seenCustomTypes.add(type);
        continue;
      }
      if (!colorValues.includes(color)) return false;
      const key = JSON.stringify([type, color]);
      if (!expectedPhysicalKeys.has(key) || seenPhysicalKeys.has(key)) return false;
      seenPhysicalKeys.add(key);
    }

    if (seenPhysicalKeys.size !== expectedPhysicalKeys.size || seenDigitalTypes.size < 1) return false;
    return true;
  };

  const resetForm = (form) => {
    const select = form?.querySelector(SELECT_SELECTOR);
    const nativeField = select?.closest('.q-field');
    const existingPicker = form?.querySelector(GENERATED_SELECTOR);
    const existingConsent = form?.querySelector(GENERATED_CONSENT_SELECTOR);

    if (typeof form?.[CONSENT_CLEANUP_KEY] === 'function') {
      form[CONSENT_CLEANUP_KEY]();
    } else {
      existingConsent?.remove();
      form?.classList.remove(CONSENT_PENDING_CLASS);
    }

    existingPicker?.remove();
    if (nativeField) nativeField.classList.remove('q-native-variant-select--enhanced');

    if (select?.[SELECT_HANDLER_KEY]) {
      select.removeEventListener('change', select[SELECT_HANDLER_KEY]);
      delete select[SELECT_HANDLER_KEY];
    }

    return { select, nativeField };
  };

  const initPicker = (root) => {
    const form = root.dataset.ownerSectionId
      ? document.getElementById('product-form-' + root.dataset.ownerSectionId)
      : document.querySelector('.q-product-layout [data-product-form]');
    const { select, nativeField } = resetForm(form);
    if (!form || !select || !nativeField) return;

    const dataNode = root.querySelector(DATA_SELECTOR);
    let config;
    try {
      config = JSON.parse(dataNode?.textContent || '');
    } catch {
      return;
    }
    if (!config || typeof config !== 'object' || Array.isArray(config)) return;
    if (!hasValidRawConfig(config) || !config.variants.every(isValidRawVariant)) return;

    const rawOptionNames = config.optionNames;
    const rawPhysicalTypes = config.physicalTypes;
    const rawColorValues = config.colorValues;
    const optionNames = trimmedList(rawOptionNames);
    const physicalTypes = trimmedList(rawPhysicalTypes);
    const colorValues = trimmedList(rawColorValues);
    const wizardConfig = window.QProductColorWizard?.configForForm(form);
    const customColorValue = wizardConfig?.customColorValue || '';
    const choiceColors = [...colorValues, ...(customColorValue && !colorValues.includes(customColorValue) ? [customColorValue] : [])];
    const preview = form.dataset.productPreviewOnly === 'true';
    const selectable = variant => Boolean(variant && (preview || variant.available));
    const firstSelectable = values => values.find(selectable) || null;
    const typeOptionName = config.typeOptionName.trim();
    const colorOptionName = config.colorOptionName.trim();
    const digitalValue = config.digitalValue.trim();
    const digitalLabel = config.digitalLabel.trim();
    const digitalConsent = config.digitalConsent.enabled
      ? {
        text: config.digitalConsent.text,
        propertyName: config.digitalConsent.propertyName.trim(),
        propertyValue: config.digitalConsent.propertyValue,
      }
      : null;
    const typeIndex = optionNames.indexOf(typeOptionName);
    const colorIndex = optionNames.indexOf(colorOptionName);
    const singleOption = optionNames.length === 1;
    const extraIndex = optionNames.length === 3
      ? optionNames.findIndex((_, index) => index !== typeIndex && index !== colorIndex) : -1;
    const variants = config.variants.map((variant) => ({
      id: String(variant.id),
      available: variant.available,
      options: variant.options.map((option) => option.trim()),
      imageUrl: variant.imageUrl == null ? '' : variant.imageUrl,
    }));

    if (!digitalLabel || !hasExactVariantStructure({
      rawOptionNames,
      optionNames,
      rawPhysicalTypes,
      physicalTypes,
      rawColorValues,
      colorValues,
      digitalValue,
      variants,
      typeIndex,
      colorIndex,
      customColorValue,
    })) return;

    const physicalVariants = variants.filter((variant) => (
      (singleOption || physicalTypes.includes(variant.options[typeIndex]))
      && choiceColors.includes(variant.options[colorIndex])
      && (variant.options[colorIndex] !== customColorValue || wizardConfig.contexts.some(context => context.valid
        && context.type === variant.options[typeIndex] && context.extra === (extraIndex < 0 ? '' : variant.options[extraIndex])))
    ));
    const digitalVariants = variants.filter((variant) => variant.options[colorIndex] === digitalValue);
    if (physicalVariants.length === 0 && digitalVariants.length === 0) return;

    const submit = digitalConsent ? form.querySelector(SUBMIT_SELECTOR) : null;
    if (digitalConsent && !submit) return;

    const picker = document.createElement('div');
    picker.className = 'q-variant-image-picker';
    picker.setAttribute('data-generated-variant-image-picker', '');

    const typeGroup = document.createElement('fieldset');
    typeGroup.className = 'q-variant-image-picker__group q-variant-image-picker__group--types';
    const typeLegend = document.createElement('legend');
    typeLegend.className = 'q-variant-image-picker__legend';
    typeLegend.textContent = typeOptionName;
    const typeChoices = document.createElement('div');
    typeChoices.className = 'q-variant-image-picker__choices q-variant-image-picker__choices--types';
    typeGroup.append(typeLegend, typeChoices);

    const colorGroup = document.createElement('fieldset');
    colorGroup.className = 'q-variant-image-picker__group q-variant-image-picker__group--colors';
    const colorLegend = document.createElement('legend');
    colorLegend.className = 'q-variant-image-picker__legend';
    colorLegend.textContent = colorOptionName;
    const colorChoices = document.createElement('div');
    colorChoices.className = 'q-variant-image-picker__choices q-variant-image-picker__choices--colors';
    colorGroup.append(colorLegend, colorChoices);

    const typeButtons = new Map();
    const colorButtons = new Map();
    const extraButtons = new Map();
    const extraGroup = document.createElement('fieldset');
    extraGroup.className = 'q-variant-image-picker__group';
    const extraChoices = document.createElement('div');
    extraChoices.className = 'q-variant-image-picker__choices q-variant-image-picker__choices--types';
    if (extraIndex >= 0) {
      const legend = document.createElement('legend');
      legend.className = 'q-variant-image-picker__legend';
      legend.textContent = optionNames[extraIndex];
      extraGroup.append(legend, extraChoices);
    }
    let activeType = '';
    let activeColor = '';
    let activeExtra = '';
    const matchesExtra = variant => extraIndex < 0 || variant.options[extraIndex] === activeExtra;

    const variantsForType = (type) => physicalVariants.filter(
      (variant) => variant.options[typeIndex] === type,
    );
    const variantFor = (type, color) => (singleOption ? variants : physicalVariants).find((variant) => (
      (singleOption || variant.options[typeIndex] === type) && variant.options[colorIndex] === color && matchesExtra(variant)
    ));

    const selectVariant = (variant) => {
      if (!selectable(variant)) return;
      select.value = variant.id;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    };

    const render = () => {
      const selectedVariant = variants.find((variant) => variant.id === String(select.value));
      if (extraIndex >= 0 && selectedVariant) activeExtra = selectedVariant.options[extraIndex];
      if (selectedVariant?.options[colorIndex] === digitalValue) {
        activeType = DIGITAL_TYPE;
        activeColor = singleOption ? digitalValue : '';
      } else if (selectedVariant && (singleOption || physicalTypes.includes(selectedVariant.options[typeIndex]))) {
        activeType = selectedVariant.options[typeIndex];
        activeColor = selectedVariant.options[colorIndex];
      }

      typeButtons.forEach((button, type) => {
        const candidates = type === DIGITAL_TYPE ? digitalVariants : variantsForType(type);
        const available = Boolean(firstSelectable(candidates));
        const selected = activeType === type;
        button.disabled = !available;
        button.setAttribute('aria-disabled', String(!available));
        button.setAttribute('aria-pressed', String(selected));
      });

      colorGroup.hidden = !singleOption && activeType === DIGITAL_TYPE;
      extraGroup.hidden = activeType === DIGITAL_TYPE;
      extraButtons.forEach((button, value) => {
        const available = physicalVariants.some(variant => selectable(variant)
          && variant.options[typeIndex] === activeType && variant.options[extraIndex] === value);
        button.disabled = !available;
        button.setAttribute('aria-disabled', String(!available));
        button.setAttribute('aria-pressed', String(activeExtra === value));
      });
      colorButtons.forEach(({ button, image }, color) => {
        const variant = variantFor(activeType, color);
        const available = selectable(variant);
        button.hidden = color === customColorValue && !variant;
        const selected = activeColor === color;
        button.disabled = !available;
        button.setAttribute('aria-disabled', String(!available));
        button.setAttribute('aria-pressed', String(selected));
        image.hidden = !variant?.imageUrl;
        if (variant?.imageUrl && image.getAttribute('src') !== variant.imageUrl) image.src = variant.imageUrl;
      });
    };

    (singleOption ? [] : [...physicalTypes, DIGITAL_TYPE]).forEach((type) => {
      const typeButton = document.createElement('button');
      typeButton.type = 'button';
      typeButton.className = 'q-variant-image-picker__type';
      typeButton.textContent = type === DIGITAL_TYPE ? digitalLabel : type;
      typeButton.addEventListener('click', () => {
        if (type === DIGITAL_TYPE) {
          selectVariant(firstSelectable(digitalVariants.filter(matchesExtra)) || firstSelectable(digitalVariants));
          return;
        }

        const sameColorVariant = variantFor(type, activeColor);
        selectVariant(
          selectable(sameColorVariant)
            ? sameColorVariant
            : firstSelectable(variantsForType(type).filter(matchesExtra)) || firstSelectable(variantsForType(type)),
        );
      });
      typeButtons.set(type, typeButton);
      typeChoices.append(typeButton);
    });

    if (extraIndex >= 0) {
      [...new Set(physicalVariants.map(variant => variant.options[extraIndex]))].forEach(value => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'q-variant-image-picker__type q-variant-image-picker__extra';
        button.textContent = value;
        button.addEventListener('click', () => {
          const candidates = variantsForType(activeType).filter(variant => variant.options[extraIndex] === value);
          selectVariant(firstSelectable(candidates.filter(variant => variant.options[colorIndex] === activeColor))
            || firstSelectable(candidates));
        });
        extraButtons.set(value, button);
        extraChoices.append(button);
      });
    }

    const choiceValues = singleOption
      ? [...colorValues, digitalValue].filter((value) => variants.some((variant) => variant.options[colorIndex] === value))
      : choiceColors;
    choiceValues.forEach((color) => {
      const choiceLabel = color === digitalValue ? digitalLabel : color;
      const colorButton = document.createElement('button');
      colorButton.type = 'button';
      colorButton.className = 'q-variant-image-picker__color';
      colorButton.setAttribute('aria-label', choiceLabel);

      const image = document.createElement('img');
      image.className = 'q-variant-image-picker__image';
      image.alt = '';
      image.width = 100;
      image.height = 100;
      image.loading = 'lazy';

      const label = document.createElement('span');
      label.className = 'q-variant-image-picker__color-label';
      label.textContent = choiceLabel;
      colorButton.append(image, label);
      colorButton.addEventListener('click', () => selectVariant(variantFor(activeType, color)));
      colorButtons.set(color, { button: colorButton, image });
      colorChoices.append(colorButton);
    });

    if (singleOption) picker.replaceChildren(colorGroup);
    else if (extraIndex >= 0) picker.replaceChildren(typeGroup, extraGroup, colorGroup);
    else picker.replaceChildren(typeGroup, colorGroup);
    nativeField.before(picker);

    const consentController = digitalConsent ? window.QProductDigitalConsent.createController({
      form,
      select,
      submit,
      variants,
      isDigital: (variant) => variant?.options[colorIndex] === digitalValue,
      digitalConsent,
    }) : null;

    const onNativeChange = () => {
      render();
      consentController?.queueSynchronization();
      window.QProductColorWizard?.synchronize(form);
    };
    select[SELECT_HANDLER_KEY] = onNativeChange;
    select.addEventListener('change', onNativeChange);

    const selectedVariant = variants.find((variant) => variant.id === String(select.value));
    const fallbackVariant = firstSelectable([...physicalVariants, ...digitalVariants]);
    if (!selectedVariant && fallbackVariant) select.value = fallbackVariant.id;
    render();
    consentController?.synchronize();

    nativeField.classList.add('q-native-variant-select--enhanced');
    root.dataset.variantImagePickerReady = 'true';
    window.QProductColorWizard?.synchronize(form);
  };

  const initWithin = (scope) => {
    if (scope?.matches?.(ROOT_SELECTOR)) initPicker(scope);
    scope?.querySelectorAll?.(ROOT_SELECTOR).forEach(initPicker);
  };

  const resetCurrentForm = () => {
    const form = document.querySelector('.q-product-layout [data-product-form]');
    resetForm(form);
  };

  const previousModule = window[MODULE_KEY];
  if (previousModule?.sectionLoadHandler) {
    document.removeEventListener('shopify:section:load', previousModule.sectionLoadHandler);
  }
  if (previousModule?.sectionUnloadHandler) {
    document.removeEventListener('shopify:section:unload', previousModule.sectionUnloadHandler);
  }

  const sectionLoadHandler = (event) => {
    const target = event.target;
    if (!target) return;

    if (target.matches?.('[data-product-color-wizard]') || target.querySelector?.('[data-product-color-wizard]')) {
      initWithin(document);
      return;
    }
    if (target.matches?.(ROOT_SELECTOR) || target.querySelector?.(ROOT_SELECTOR)) {
      initWithin(target);
      return;
    }

    if (target.matches?.('[data-product-form]') || target.querySelector?.('[data-product-form]')) {
      initWithin(document);
    }
  };
  const sectionUnloadHandler = (event) => {
    const target = event.target;
    if (!target) return;

    const form = target.matches?.('[data-product-form]')
      ? target
      : target.querySelector?.('[data-product-form]');
    if (form) {
      resetForm(form);
      return;
    }

    if (target.matches?.(ROOT_SELECTOR) || target.querySelector?.(ROOT_SELECTOR)) {
      resetCurrentForm();
    }
  };

  document.addEventListener('shopify:section:load', sectionLoadHandler);
  document.addEventListener('shopify:section:unload', sectionUnloadHandler);
  window[MODULE_KEY] = { sectionLoadHandler, sectionUnloadHandler };

  initWithin(document);
})();
